/* eslint-disable no-promise-executor-return */
/* eslint-disable no-plusplus */
import { Link, useLocation } from "react-router-dom";
import { FunctionComponent, useContext, useEffect, useState } from "react";
import dayjs, { Dayjs } from "dayjs";
import styles from "./self-service.module.scss";
import { initialPaginationData } from "../utils/constants";
import Table, { Column } from "../components/table/Table";
import LayoutContext from "../context/layout-context";
import Modal, { ModalProps } from "../components/modal/Modal";
import { resources } from "../utils/resources";
import ContextMenu, {
  ContextOption
} from "../components/context-menu/ContextMenu";
import UserContext from "../context/user-context";
import { downloadFile, getHistory } from "../utils/helpers/data/task-history";
import { BooleanFilter } from "../utils/helpers/types-helpers";
import { Historys } from "../utils/helpers/types";
import Button from "../components/button/Button";
import style from "./history.module.scss";
import RangePicker from "../components/range-picker/RangePicker";
import Select, { Option } from "../components/select/Select";
import { axiosInstanceTasks } from "../utils/helpers/axios";

const statusMap: Record<string, string> = {
  COMPLETED: "success",
  FAILED: "error"
};
const statusOptions: Option[] = [
  {
    label: "COMPLETED",
    value: "COMPLETED"
  },
  {
    label: "FAILED",
    value: "FAILED"
  },
  {
    label: "PENDING",
    value: "PENDING"
  },
  {
    label: "STARTED",
    value: "STARTED"
  },
  {
    label: "DUPLICATE",
    value: "DUPLICATE"
  },
  {
    label: "INCOMPLETE",
    value: "INCOMPLETE"
  },
  {
    label: "ERROR",
    value: "ERROR"
  },
  {
    label: "INTERRUPTED",
    value: "INTERRUPTED"
  }
];

const dateFilterOptions: Option[] = [
  {
    label: "Processing Time",
    value: "processingTime"
  },
  {
    label: "File Reference Date",
    value: "fileReferenceDate"
  },
  {
    label: "Start Time",
    value: "runningStartTime"
  },
  {
    label: "End Time",
    value: "runningEndTime"
  }
];
const TaskHistory: FunctionComponent = () => {
  let timerRef: NodeJS.Timeout;
  const [pagination, setPagination] = useState({ ...initialPaginationData });
  const [loading, setLoading] = useState(false);
  const { notify } = useContext(LayoutContext);
  const [history, setHistory] = useState<Historys[]>([]);
  const [historyOnView, setHistoryOnView] = useState<Historys | null>(null);
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const taskNameParam = searchParams.get("taskName");
  const taskIdParam = searchParams.get("id");
  const [dateRange, setDateRange] = useState<[Dayjs | null, Dayjs | null]>([
    null,
    null
  ]);
  const [status, setStatus] = useState<string | undefined>(undefined);
  const [taskName, setTaskName] = useState<string | undefined>(undefined);
  const [dateFilterColumn, setDateFilterColumn] = useState<string | undefined>(
    undefined
  );
  const showResetFilter = status || dateRange[0] || dateRange[1] || taskName;
  const [reset, setReset] = useState(false);

  const resetFilters = () => {
    setReset(true);
    setDateRange([null, null]);
    setStatus("");
    setDateFilterColumn(undefined);
    setTaskName("");
    setPagination({ ...pagination, pageNumber: 1 });
  };

  const formatDateForApi = (date: Dayjs | null) => {
    return date ? date.format("YYYY-MM-DD") : "";
  };

  const pageTitle = taskNameParam ? `${taskNameParam} History` : "History";

  const fetchHistory = async (
    taskName: string,
    pageNumber: number = pagination.pageNumber,
    pageSize: number = pagination.pageSize
  ) => {
    setLoading(true);
    setReset(false);

    let formattedEndTime;

    const formattedStartTime = formatDateForApi(dateRange[0]);

    formattedEndTime = formatDateForApi(dateRange[1]);
    if (formattedEndTime.toString() !== "") {
      formattedEndTime = formatDateForApi(dateRange[1]).concat(" 23:59:00");
    }

    try {
      const { data, error, message } = await getHistory(
        taskName,
        pageNumber,
        pageSize,
        status,
        formattedStartTime,
        formattedEndTime,
        dateFilterColumn
      );

      if (error || !data) {
        notify("error", `Unable to fetch history: ${message}`);
        return;
      }

      setHistory(data.records);
      setPagination({
        ...pagination,
        pageNumber,
        total: data.total
      });
    } catch (error) {
      notify("error", `An error occurred while fetching history: ${error}`);
    } finally {
      setLoading(false);
    }
  };

  const columns: Column<Historys>[] = [
    {
      title: "Task Name",
      dataIndex: "taskName",
      key: "taskName"
    },
    {
      title: "Start Time",
      dataIndex: "runningStartTime",
      key: "runningStartTime",
      render: (runningStartTime: string | undefined) =>
        runningStartTime
          ? dayjs(runningStartTime).format("YYYY-MM-DD HH:mm:ss")
          : "N/A"
    },
    {
      title: "End Time",
      dataIndex: "runningEndTime",
      key: "runningEndTime",
      render: (runningEndTime: string | undefined) =>
        runningEndTime
          ? dayjs(runningEndTime).format("YYYY-MM-DD HH:mm:ss")
          : "N/A"
    },
    {
      title: "File Reference Date",
      dataIndex: "fileReferenceDate",
      key: "fileReferenceDate",
      render: (fileReferenceDate: string | undefined) =>
        fileReferenceDate
          ? dayjs(fileReferenceDate).format("YYYY-MM-DD HH:mm:ss")
          : "N/A"
    },
    {
      title: "Processing Time",
      dataIndex: "processingTime",
      key: "processingTime",
      render: (processingTime: string | undefined) =>
        processingTime
          ? dayjs(processingTime).format("YYYY-MM-DD HH:mm:ss")
          : "N/A"
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (value: string) => (
        <span className={`tag ${statusMap[value]}`}>{value}</span>
      )
    },
    {
      title: "",
      render: (_: unknown, history: Historys) => {
        const options: ContextOption[] = [
          {
            label: "View Details",
            onClick: () => setHistoryOnView(history)
          },
          history.status === "FAILED" && {
            label: "Retry",
            onClick: () => {
              handleRetry(history.id);
            }
          }
        ].filter(BooleanFilter) as ContextOption[];
        return <ContextMenu options={options} />;
      }
    }
  ];

  useEffect(() => {
    if (taskNameParam !== null) {
      fetchHistory(taskNameParam);
    }
  }, [taskIdParam, taskNameParam, pagination.pageSize]);

  useEffect(() => {
    if (taskNameParam !== null) {
      clearTimeout(timerRef);
      timerRef = setTimeout(() => {
        fetchHistory(taskNameParam);
      }, 400);
    }
  }, [taskNameParam]);

  useEffect(() => {
    fetchHistory(taskNameParam || "");
  }, [pagination.pageSize, reset]);

  const handleViewReport = async () => {
    setLoading(true);
    try {
      setPagination({ ...pagination, pageNumber: 1 });

      if (dateRange[0] && dateRange[1]) {
        await fetchHistory(taskNameParam || "", 1);
      } else if (status) {
        await fetchHistory(taskNameParam || "", 1);
      } else if (taskName) {
        await fetchHistory(taskNameParam || "", 1);
      }
      if (dateRange[0] && dateRange[1] && !dateFilterColumn) {
        notify("error", "Please select a filter option for the date range.");
        setLoading(false);
        return;
      }

      if (dateRange[0] && !dateRange[1]) {
        notify("error", "Kindly select the two dates.");
        setLoading(false);
        return;
      }

      setLoading(false);
    } catch (error) {
      setLoading(false);
      notify("error", "An error occurred while fetching history.", error);
    }
  };

  const handleRetry = async (historyId: number | undefined) => {
    if (historyId === undefined) {
      notify("error", "Invalid history ID for retry");
      return;
    }
    try {
      setLoading(true);
      const response = await axiosInstanceTasks.post(
        `/history/retry/${historyId}`
      );
      if (response.data.message === "Success") {
        notify("success", response.data.data);
      } else {
        notify("error", response.data.data || "Retry failed");
      }
    } catch (error) {
      notify("error", `An error occurred during retry: ${error}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <main>
      <h1 className="title vertical-margin spaced thin">{pageTitle}</h1>
      <br />
      <br />
      <div className="vertical-margin spaced">
        <div className={style.cont}>
          <div className={style.container}>
            <div className={style.cont1}>
              <div className="input-group">
                <RangePicker
                  value={dateRange}
                  onChange={setDateRange}
                  format="D MMM YYYY"
                  placeholder="Select date range"
                />
              </div>

              <div className="input-group">
                <Select
                  options={dateFilterOptions}
                  value={dateFilterColumn || ""}
                  onSelect={value => setDateFilterColumn(value as string)}
                  placeholder="Select Date Filter"
                  responsive
                />
              </div>

              <div className="input-group ">
                <Select
                  options={statusOptions}
                  value={status || ""}
                  onSelect={value => setStatus(value as string)}
                  placeholder="Select Status"
                  responsive
                />
              </div>
            </div>
          </div>

          <div className={style.inputView}>
            <Button
              type="accent"
              size="large"
              className="strong no-pdf-print"
              onClick={handleViewReport}
            >
              Filter record
            </Button>
            <span className={style.button} />
          </div>
        </div>

        {showResetFilter && (
          <Button type="accent" size="small" onClick={resetFilters}>
            RESET FILTERS
          </Button>
        )}
      </div>
      <Table
        dataSource={history || []}
        columns={columns}
        pagination={pagination}
        onPageChange={(pageNumber: number) =>
          fetchHistory(taskNameParam || "", pageNumber)
        }
        onPageSizeChange={pageSize =>
          setPagination({ ...pagination, pageSize, pageNumber: 1 })
        }
        loading={loading}
        className="smaller"
      />

      <TaskViewModal
        history={historyOnView}
        cancel={() => setHistoryOnView(null)}
        refresh={(taskName: string, pageNumber?: number) =>
          fetchHistory(taskName, pageNumber)
        }
        visible={Boolean(historyOnView)}
      />
      <br />
      <br />
      <Button>
        <Link to="/task-creation">Back to Task</Link>
      </Button>
    </main>
  );
};

interface TaskEditModalProps {
  history: Historys | null;
  cancel: () => void;
  refresh: (taskName: string, pageNumber?: number) => void;
  visible: boolean;
}

const TaskViewModal: FunctionComponent<TaskEditModalProps> = ({
  history: _history,
  cancel,
  visible,
  refresh
}) => {
  const [loading, setLoading] = useState(false);
  const [history, setHistory] = useState<Historys | null>(_history);
  const { user } = useContext(UserContext);

  useEffect(() => {
    setHistory(_history);
  }, [_history]);

  return (
    <Modal visible={visible} cancel={cancel} ignoreBackdropClicks>
      {loading ? (
        <img
          alt="loading"
          src={resources.images.LOADING_TYPING}
          className="generic-icon xxl"
        />
      ) : (
        <section className={styles.details}>
          <div className={styles.detail}>
            <span className={styles.key}>ID</span>
            <span className={styles.value}>{history?.id || "N/A"}</span>
          </div>
          <div className={styles.detail}>
            <span className={styles.key}>Task Name</span>
            <span className={styles.value}>{history?.taskName || "N/A"}</span>
          </div>
          <div className={styles.detail}>
            <span className={styles.key}>Running End Time:</span>
            <span className={styles.value}>
              {history?.runningEndTime || "N/A"}
            </span>
          </div>
          <div className={styles.detail}>
            <span className={styles.key}>Running Start Time:</span>
            <span className={styles.value}>
              {history?.runningStartTime || "N/A"}
            </span>
          </div>
          <div className={styles.detail}>
            <span className={styles.key}>Status:</span>
            <span className={styles.value}>{history?.status || "N/A"}</span>
          </div>
          <div className={styles.detail}>
            <span className={styles.key}>Summary:</span>
            <span
              className={styles.value}
              dangerouslySetInnerHTML={{
                __html: history?.summary?.replace(/\n/g, "<br>") || "N/A"
              }}
            />
          </div>
          <div className={styles.detail}>
            <span className={styles.key}>Processing Time:</span>
            <span className={styles.value}>
              {history?.processingTime || "N/A"}
            </span>
          </div>
          <div className={styles.detail}>
            <span className={styles.key}>File Reference Date:</span>
            <span className={styles.value}>
              {history?.fileReferenceDate || "N/A"}
            </span>
          </div>
          <div className={styles.detail}>
            <span className={styles.key}>Records Processed</span>
            <span className={styles.value}>
              {history?.recordsProcessed || "N/A"}
            </span>
          </div>
          <div className={styles.detail}>
            <span className={styles.key}>Created On</span>
            <span className={styles.value}>{history?.createdOn || "N/A"}</span>
          </div>
          <div className={styles.detail}>
            <span className={styles.key}>Last Updated On</span>
            <span className={styles.value}>
              {history?.lastUpdatedOn || "N/A"}
            </span>
          </div>
          <div className={styles.detail}>
            <span className={styles.key}>Task ClassName</span>
            <span className={styles.value}>
              {history?.taskClassName || "N/A"}
            </span>
          </div>

          <div className={styles.detail}>
            <span className={styles.key}>Retry Count</span>
            <span className={styles.value}>{history?.retryCount || "N/A"}</span>
          </div>

          <div className={styles.detail}>
            <span className={styles.key}>Last LoadTime</span>
            <span className={styles.value}>
              {history?.lastLoadTime || "N/A"}
            </span>
          </div>

          <div className={styles.detail}>
            <span className={styles.key}>Label</span>
            <span className={styles.value}>{history?.label || "N/A"}</span>
          </div>
        </section>
      )}
    </Modal>
  );
};

export default TaskHistory;
